"use client";

import { FC } from "react";
import style from "./style.module.scss";
import { IWeatherForecastData } from "@/interfaces/Map";
import ForecastDetailsItem from "./ForecastDetailsItem";
import MetarDetailsItem from "./MetarDetailsItem";
import NoDataSection from "./NoDataSection";
import {
  AltitudeUnitConverter,
  AltitudeUnit,
  TemperatureConverter,
  Temperature,
  WindSpeedConverter,
  WindSpeed,
  formatTemperature,
} from "@/helpers/unit-conversions";
import { formatTime } from "@/helpers/date-helpers";
import {
  ForecastDetailsItems,
  ForecastMetarDetailsItems,
  GENERAL,
} from "@/constants";
import { Nullable } from "@/types";

type Props = {
  forecast: Nullable<IWeatherForecastData>;
};

const formatElevation = (
  elevation: Nullable<number>,
  unit: Nullable<string>
): string => {
  if (elevation === null || !unit) return "";
  const elevationInFeet = elevation;
  const backendUnit = unit;
  const elevationInMeters = AltitudeUnitConverter.fromUnit(
    elevationInFeet,
    backendUnit,
    AltitudeUnit.meters
  );
  const metersRounded = Math.round(elevationInMeters);
  const feetRounded = Math.round(elevationInFeet);
  return `${metersRounded} meters / ${feetRounded} ft`;
};

const formatWindSpeed = (
  speed: Nullable<number>,
  unit: Nullable<string>
): string => {
  if (speed === null || !unit) return "";
  const speedInKnots = WindSpeedConverter.fromUnit(speed, unit, WindSpeed.kts);
  const knotsRounded = Math.round(speedInKnots);
  return `${knotsRounded} kt`;
};

const ForecastDetails: FC<Props> = ({ forecast }) => {
  const formatTimeWithTimezone = (dateString: string | undefined) => {
    return formatTime(dateString, forecast?.timezoneId || forecast?.timezone);
  };

  return (
    <div className={style.foreCastDetails}>
      {forecast ? (
        <>
          <div className={style.section}>
            {ForecastDetailsItems.slice(
              0,
              GENERAL.FORECAST_DETAILS_FIRST_SECTION_COUNT
            ).map((item, index) => (
              <ForecastDetailsItem
                key={index}
                label={item.label}
                value={item.getValue(forecast, formatTimeWithTimezone)}
                iconSrc={item.iconSrc}
              />
            ))}

            <div className={style.columnSection}>
              {ForecastDetailsItems.slice(
                GENERAL.FORECAST_DETAILS_FIRST_SECTION_COUNT
              ).map((item, index) => (
                <ForecastDetailsItem
                  key={index + GENERAL.FORECAST_DETAILS_FIRST_SECTION_COUNT}
                  iconSrc={item.iconSrc}
                  label={item.label}
                  value={item.getValue(forecast, formatTimeWithTimezone)}
                />
              ))}
            </div>
          </div>

          <div className={style.section}>
            <div className={style.columnSection}>
              {ForecastMetarDetailsItems.map((item, index) => (
                <MetarDetailsItem
                  key={index}
                  iconSrc={item.iconSrc}
                  label={item.label}
                  value={item.getValue(
                    forecast,
                    formatTimeWithTimezone,
                    formatWindSpeed
                  )}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <NoDataSection />
      )}
    </div>
  );
};

export default ForecastDetails;
